import React, { FC, useMemo } from 'react';
import { GenericMedia } from '@/declarations/GenericMedia';
import { Page } from '@/declarations/models/Page';
import Container from '@/components/Container';
import Styles from '@/assets/js/Styles';
import { Box, IconButton, List, ListItem, Tooltip, Typography } from '@mui/material';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { useTranslation } from 'react-i18next';
import Image from '@/components/Image';
import Grid from '@mui/material/Grid';
import { formatAPITimestamp, isApiTimestampAtOrAfter } from '@/utils/dates';
import { Status } from '@/declarations/models/Status';

type DisplayStatus = Status | 'planned_publish';

const getDisplayStatus = (page: Page): DisplayStatus => {
  if (page.status === Status.PUBLISHED) {
    const isPlannedPublish = !!page.published_at && isApiTimestampAtOrAfter(page.published_at);

    if (isPlannedPublish) {
      return 'planned_publish';
    }
  }

  return page.status;
};

const getDisplayStatusText = (displayStatus: DisplayStatus, tCommon: (key: string) => string): string => {
  if (displayStatus === 'planned_publish') {
    return tCommon('Status.planned_publishing');
  }

  if (displayStatus === Status.PUBLISHED) {
    return tCommon('Status.published');
  }

  if (displayStatus === Status.DRAFT) {
    return tCommon('Status.not_published');
  }

  if (displayStatus === Status.ARCHIVED) {
    return tCommon('Status.archived');
  }

  return '';
};

const Details = ({ item }: { item: GenericMedia<Page> }) => {
  const { t: tCommon } = useTranslation('common');
  const displayStatus = useMemo(() => getDisplayStatus(item.source), [item.source]);
  const pageStatusText = useMemo(() => getDisplayStatusText(displayStatus, tCommon), [displayStatus, tCommon]);

  return (
    <Container column spaceBetween left fullWidth fullHeight>
      <Typography variant='body2' sx={{ fontWeight: 500 }}>
        {item.title}
      </Typography>

      <List dense>
        {item.source.event_data ? (
          <>
            {item.source.event_data.time_periods?.map((tp, i) => (
              <ListItem
                /* eslint-disable-next-line react/no-array-index-key */
                key={i}
                sx={{
                  display: 'flex',
                  gap: 1,
                  p: 0,
                }}>
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  {tCommon('date')}:
                </Typography>
                <Typography variant='body2'>{formatAPITimestamp(tp.start_date)}</Typography>
              </ListItem>
            ))}
            <ListItem
              sx={{
                display: 'flex',
                gap: 1,
                p: 0,
              }}>
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                {tCommon('place')}:
              </Typography>
              <Typography variant='body2'>{item.source.location?.title}</Typography>
            </ListItem>
          </>
        ) : (
          <>
            <ListItem
              sx={{
                display: 'flex',
                gap: 1,
                p: 0,
              }}>
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                {tCommon('lastEdited')}:
              </Typography>
              <Typography variant='body2'>{formatAPITimestamp(item.source.created_at)}</Typography>
            </ListItem>
            <ListItem
              sx={{
                display: 'flex',
                gap: 1,
                p: 0,
              }}>
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                {tCommon('publishDate')}:
              </Typography>
              <Typography variant='body2'>
                {formatAPITimestamp(item.source.published_at || '', 'date') || '-'}
              </Typography>
            </ListItem>
            <ListItem
              sx={{
                display: 'flex',
                gap: 1,
                p: 0,
              }}>
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                {tCommon('status')}:
              </Typography>
              <Typography variant='body2'>{pageStatusText}</Typography>
            </ListItem>
          </>
        )}
      </List>
    </Container>
  );
};

const ImagePreview = ({ item }: { item: GenericMedia<Page> }) => {
  let imgMimeType = '';
  if (item?.mimetype?.includes('png')) {
    imgMimeType = `mediaType=${item.mimetype}`;
  }
  return (
    <Box
      sx={{
        height: '100px',
        width: '150px',
      }}>
      <Image
        src={`${item.url}?&dimension=250x250&${imgMimeType}`}
        sx={{
          overflow: 'hidden',
          objectFit: 'cover',
          height: '100%',
          width: '100%',
        }}
      />
    </Box>
  );
};

export interface M24PageFinderSelectedItemProps {
  item: GenericMedia<Page>;
  onRemove: () => void;
}

export const M24PageFinderSelectedItem: FC<M24PageFinderSelectedItemProps> = ({ item, onRemove }) => {
  const { t: tCommon } = useTranslation('common');

  return (
    <Grid
      container
      sx={{
        py: 2,
        borderBottom: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
      }}>
      <Grid
        size={{ xs: 11 }}
        sx={{
          display: 'flex',
          gap: 2,
        }}>
        <Box>
          <ImagePreview item={item} />
        </Box>

        <Box sx={{ flexGrow: 1 }}>
          <Details item={item} />
        </Box>
      </Grid>

      <Grid size={{ xs: 1 }}>
        <Tooltip title={tCommon('remove')}>
          <IconButton onClick={onRemove}>
            <MaterialSymbol name='cancel' fill />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );
};
